import { ownerListQuerySchema, type TransferAssetOwnershipInput } from '@chukua/common';
import { DatePicker, Form, Modal, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { listOwners, type OwnerListItem } from '../owners.api';

interface TransferOwnershipModalProps {
  open: boolean;
  submitting: boolean;
  onTransfer: (input: TransferAssetOwnershipInput, ownerName: string) => Promise<boolean>;
  onCancel: () => void;
}

interface FormValues {
  ownerId: string;
  effectiveFrom: Dayjs;
}

const OWNER_PAGE_SIZE = 100;

/**
 * Owner picker + effective date — shared by the truck and trailer Ownership tabs (an owner can
 * hold both, gitaumoses4/chukua#15). Owners rarely run past a hundred in V1, so one fetch on
 * open is enough (no remote search yet, unlike a paged list screen).
 */
export function TransferOwnershipModal({ open, submitting, onTransfer, onCancel }: TransferOwnershipModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [owners, setOwners] = useState<OwnerListItem[]>([]);

  useEffect(() => {
    if (!open) return;
    listOwners(ownerListQuerySchema.parse({ pageSize: OWNER_PAGE_SIZE })).then((page) => setOwners(page.items));
  }, [open]);

  async function submit() {
    const values = await form.validateFields();
    const owner = owners.find((o) => o.id === values.ownerId);
    const transferred = await onTransfer({ ownerId: values.ownerId, effectiveFrom: values.effectiveFrom.format('YYYY-MM-DD') }, owner?.name ?? 'the owner');
    if (transferred) form.resetFields();
  }

  return (
    <Modal open={open} title="Transfer ownership" onOk={submit} onCancel={onCancel} confirmLoading={submitting} destroyOnClose okText="Transfer">
      <Form<FormValues> form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="ownerId" label="New owner" rules={[{ required: true, message: 'Select an owner' }]}>
          <Select options={owners.map((owner) => ({ value: owner.id, label: owner.name }))} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="effectiveFrom" label="Effective from" rules={[{ required: true, message: 'Select an effective date' }]}>
          <DatePicker className="w-full" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
